import React from 'react';
import { InlineNotification, Button } from 'carbon-components-react';
import { Renew16 } from '@carbon/icons-react';
import {
  LOOKUP_TIMEOUT_ERROR,
  LOOKUP_TIMEOUT_SUPPORT_LINE,
  LOOKUP_TIMEOUT_SUBTITLE,
  LOOKUP_TIMEOUT_TITLE
} from '../constants/errorMessages';

interface ErrorStepProps {
  error: string | null;
  onStartOver: () => void;
  /** Only provided when in PAYMENT_EXPIRED state — retries checkout with the same lookupId */
  onRetryPayment?: () => void;
}

const ErrorStep: React.FC<ErrorStepProps> = ({
  error,
  onStartOver,
  onRetryPayment
}) => {
  // LOOKUP_TIMEOUT is set when member-lookup status polling exceeds MAX_POLL_DURATION_MS.
  // TIMEOUT is used for file-validation polling on the upload step.
  const isLookupTimeout = error?.startsWith(LOOKUP_TIMEOUT_ERROR);
  const isTimeout = isLookupTimeout || error?.startsWith('TIMEOUT:');
  // EXPIRED prefix indicates the Stripe checkout session timed out.
  const isExpired = error?.startsWith('EXPIRED:');

  const message = isLookupTimeout
    ? null
    : isTimeout
    ? (error ?? '').replace('TIMEOUT:', '').trim()
    : isExpired
    ? (error ?? '').replace('EXPIRED:', '').trim()
    : error;

  if (isExpired) {
    return (
      <div className="byda-member-lookup__error-step">
        <InlineNotification
          kind="warning"
          title="Payment session expired"
          subtitle={
            message ?? 'Your payment session has expired. Please try again.'
          }
          lowContrast
          hideCloseButton
        />
        <div className="byda-member-lookup__error-step__actions">
          {onRetryPayment && (
            <Button
              kind="primary"
              renderIcon={Renew16}
              onClick={onRetryPayment}
            >
              Try again
            </Button>
          )}
          <Button kind="secondary" onClick={onStartOver}>
            Start over
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="byda-member-lookup__error-step">
      <InlineNotification
        kind={isTimeout ? 'warning' : 'error'}
        title={
          isLookupTimeout
            ? LOOKUP_TIMEOUT_TITLE
            : isTimeout
            ? 'Your lookup is still processing'
            : 'Something went wrong'
        }
        subtitle={
          isLookupTimeout ? (
            <>
              {LOOKUP_TIMEOUT_SUBTITLE}
              <br />
              <br />
              {LOOKUP_TIMEOUT_SUPPORT_LINE}
            </>
          ) : (
            message ?? 'An unexpected error occurred. Please try again.'
          )
        }
        lowContrast
        hideCloseButton
      />
      {!isTimeout && (
        <div className="byda-member-lookup__error-step__actions">
          <Button kind="secondary" renderIcon={Renew16} onClick={onStartOver}>
            Start over
          </Button>
        </div>
      )}
    </div>
  );
};

export default ErrorStep;
